import { QuantitySelectorUpdateEvent } from '@theme/events';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {object} BundleItem
 * @property {string} id - Variant ID
 * @property {number} quantity
 * @property {Record<string, string>} [properties] - Line item properties
 */

/**
 * @typedef {object} BundleSelection
 * @property {string} productId
 * @property {number} quantity - Number of watches in the set
 * @property {string} label - Option name, e.g. "The Heir Set"
 * @property {string} total - Formatted total, e.g. "€78.40"
 * @property {string} compare - Formatted crossed-out price, or ''
 * @property {number} percent - Saving in %, or 0
 * @property {BundleItem[]} items - Cart lines for the set (one per colour)
 * @property {boolean} available - Every watch in the set can be bought
 * @property {boolean} mixed - The set holds more than one colour
 */

/**
 * <hh-bundle-picker> — Single / The Heir Set picker with a colour per watch.
 *
 * - Each card holds one row per watch (`[data-hh-slot]`) with photo
 *   swatches. The first watch is shared between the cards: picking its colour
 *   in one card picks it in the other, and selects that variant in Horizon's
 *   (hidden) variant picker so the gallery shows the same colour.
 * - The second watch of The Heir Set follows the first one until the shopper
 *   picks a colour for it.
 * - "Add to cart" posts the set to /cart/add.js (one line per colour) and
 *   fires the same cart event as Horizon's product form, so the cart drawer
 *   and cart count update themselves; then the drawer opens.
 * - Express checkout uses Horizon's product form (one variant × quantity):
 *   the quantity is kept in sync, and the section gets `hh-mixed-set` while
 *   the set holds two colours so the CSS can hide express checkout.
 *
 * - "Who is it for?" chips and the gift note live in one block that moves to
 *   the selected card, right above its add button. They become cart line
 *   properties: "Gift for" on every watch, "Gift note" on the first one.
 *   A chip tapped again is unselected; nothing chosen adds nothing.
 *
 * - Phones (up to 749px): one add button (`[data-hh-mobile-button]`) under
 *   the cards follows the selected card and adds the same set. The selected
 *   card's watch rows and the chips/gift note move into the "Personalise"
 *   fold below it; they move back into the cards on wider screens.
 *
 * Fires `hh:bundle-change` (bubbles) with a BundleSelection in `detail`.
 */
export class HHBundlePicker extends HTMLElement {
  static changeEvent = 'hh:bundle-change';

  #abortController = new AbortController();
  #busy = false;
  #phone = window.matchMedia('(max-width: 749px)');
  /** The "Who is it for?" choice before the current click, to unselect a chip tapped twice. */
  #recipient = '';

  connectedCallback() {
    const { signal } = this.#abortController;

    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('input', this.#handleInput, { signal });

    // Re-apply the quantity right before Horizon's form submits (express checkout).
    this.#productForm?.addEventListener('submit', this.#syncQuantity, { signal, capture: true });

    // Browsers can restore radio state on back/forward navigation.
    window.addEventListener(
      'pageshow',
      () => {
        this.#readRecipient();
        this.#apply();
      },
      { signal }
    );

    // New prices after the variant changes (see hh-variant-refresh.js).
    this.#section?.addEventListener('hh:variant-refresh', this.#handleVariantRefresh, { signal });

    // Crossing the phone breakpoint moves the watch rows and extras between the cards and the fold.
    this.#phone.addEventListener('change', () => this.#apply(), { signal });

    // Older in-app browsers (e.g. Instagram on iOS < 15.4) don't support CSS :has(),
    // so mark the buy buttons with a class too; the CSS hides Horizon's quantity box with either.
    this.#productForm?.closest('.buy-buttons-block')?.classList.add('hh-has-bundle-picker');

    this.#readRecipient();
    this.#apply();
  }

  #readRecipient() {
    const checked = this.querySelector('[data-hh-recipient]:checked');
    this.#recipient = checked instanceof HTMLInputElement ? checked.value : '';
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  /** @returns {BundleSelection | null} */
  get selection() {
    const input = this.#checkedInput;
    const option = this.#selectedOption;
    if (!input || !option) return null;

    const quantity = Number(input.value) || 1;
    const slots = this.#slots(option);

    /** @type {Map<string, number>} */
    const counts = new Map();
    let available = true;

    if (slots.length) {
      for (const slot of slots) {
        const id = slot.dataset.variantId ?? this.dataset.variantId ?? '';
        counts.set(id, (counts.get(id) ?? 0) + 1);
        if (this.#swatchFor(slot, id)?.dataset.available === 'false') available = false;
      }
    } else {
      counts.set(this.dataset.variantId ?? '', quantity);
    }

    return {
      productId: this.dataset.productId ?? '',
      quantity,
      label: input.dataset.label ?? '',
      total: input.dataset.total ?? '',
      compare: input.dataset.compare ?? '',
      percent: Number(input.dataset.percent) || 0,
      items: this.#withGiftNote(
        this.#withGiftOptions([...counts].map(([id, count]) => ({ id, quantity: count }))),
        quantity
      ),
      available,
      mixed: counts.size > 1,
    };
  }

  /**
   * Adds the selected set (or the set with the given number of watches) to the cart.
   * Used by the card buttons, the phone add button and the sticky bar.
   * @param {number} [quantity]
   * @returns {Promise<boolean>} true when the watches were added
   */
  async addToCart(quantity) {
    if (this.#busy) return false;

    if (quantity) this.#select(quantity);
    const selection = this.selection;
    if (!selection || !selection.available || !selection.items.length) return false;

    this.#setBusy(true);
    this.#showError('');
    try {
      const added = await this.#addItems(selection.items, selection.quantity);
      if (added) this.#afterAdd();
      return added;
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Selects the set with the given number of watches, e.g. 2 for The Heir Set.
   * Used by the offer note (<hh-scroll-to-buy data-select-set="2">).
   * @param {number} quantity
   */
  selectSet(quantity) {
    this.#select(quantity);
  }

  /**
   * The Heir Set brings its secret gift: a free gift line when a gift product
   * is set, otherwise a note on the first line. Either way it shows in the
   * cart, at checkout and on the order.
   * @param {BundleItem[]} items
   * @param {number} quantity
   * @returns {BundleItem[]}
   */
  #withGiftNote(items, quantity) {
    // A real gift product: added as its own free line (hh-gift-sync.js keeps it in step).
    const giftId = this.dataset.giftVariantId;
    if (giftId && quantity >= 2) return [...items, { id: giftId, quantity: 1 }];

    const name = this.dataset.giftProperty;
    const first = items[0];
    if (quantity < 2 || !name || !first) return items;
    first.properties = { ...first.properties, [name]: this.dataset.giftValue || '✓' };
    return items;
  }

  /**
   * "Gift for: Dad" on every watch and "Gift note: …" on the first one.
   * Nothing is added when nothing was chosen.
   * @param {BundleItem[]} items
   * @returns {BundleItem[]}
   */
  #withGiftOptions(items) {
    const forName = this.dataset.giftForProperty;
    const noteName = this.dataset.giftNoteProperty;
    const recipient = this.querySelector('[data-hh-recipient]:checked');
    const forValue = recipient instanceof HTMLInputElement ? recipient.value.trim() : '';
    const note = this.querySelector('[data-hh-note]');
    const noteValue = note instanceof HTMLTextAreaElement ? note.value.trim() : '';

    return items.map((item, index) => {
      /** @type {Record<string, string>} */
      const properties = {};
      if (forName && forValue) properties[forName] = forValue;
      if (noteName && noteValue && index === 0) properties[noteName] = noteValue;
      return Object.keys(properties).length ? { ...item, properties } : item;
    });
  }

  // ---------- DOM helpers ----------

  /** @returns {HTMLElement | null} */
  get #section() {
    return this.closest('.shopify-section');
  }

  /** @returns {HTMLInputElement | null} */
  get #checkedInput() {
    return this.querySelector('.hh-set-card__input:checked');
  }

  /** @returns {HTMLElement | null} */
  get #selectedOption() {
    const input = this.#checkedInput;
    return input ? input.closest('.hh-set-option') : null;
  }

  /** @returns {HTMLElement | null} */
  get #productForm() {
    const scope = this.#section ?? document;
    const productId = this.dataset.productId;
    return (
      scope.querySelector(`product-form-component[data-product-id="${productId}"]`) ??
      scope.querySelector('product-form-component')
    );
  }

  /**
   * @param {Element} option
   * @returns {HTMLElement[]}
   */
  #slots(option) {
    const details = this.#detailsOf(option);
    return details ? /** @type {HTMLElement[]} */ ([...details.querySelectorAll('[data-hh-slot]')]) : [];
  }

  /**
   * A card's watch rows, also while they sit in the phone "Personalise" fold.
   * @param {Element} option
   * @returns {HTMLElement | null}
   */
  #detailsOf(option) {
    const quantity = option instanceof HTMLElement ? option.dataset.quantity ?? '' : '';
    const own = option.querySelector('.hh-set-card__details');
    if (own instanceof HTMLElement) {
      own.dataset.hhDetails = quantity;
      return own;
    }
    const moved = this.querySelector(`[data-hh-details="${CSS.escape(quantity)}"]`);
    return moved instanceof HTMLElement ? moved : null;
  }

  /**
   * The card a watch row belongs to.
   * @param {Element} element
   * @returns {HTMLElement | null}
   */
  #optionOf(element) {
    const option = element.closest('.hh-set-option');
    if (option instanceof HTMLElement) return option;
    const details = element.closest('[data-hh-details]');
    if (!(details instanceof HTMLElement)) return null;
    const owner = this.querySelector(`.hh-set-option[data-quantity="${CSS.escape(details.dataset.hhDetails ?? '')}"]`);
    return owner instanceof HTMLElement ? owner : null;
  }

  /**
   * @param {Element} slot
   * @param {string} variantId
   * @returns {HTMLElement | null}
   */
  #swatchFor(slot, variantId) {
    return slot.querySelector(`.hh-swatch[data-variant-id="${CSS.escape(variantId)}"]`);
  }

  // ---------- events ----------

  #handleChange = (/** @type {Event} */ event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.matches('[data-hh-recipient]')) {
      this.#recipient = event.target.checked ? event.target.value : '';
      return;
    }
    if (!event.target.classList.contains('hh-set-card__input')) return;
    this.#apply();
    this.#syncGallery();
  };

  /** Live character count for the gift note. */
  #handleInput = (/** @type {Event} */ event) => {
    const note = event.target;
    if (!(note instanceof HTMLTextAreaElement) || !note.matches('[data-hh-note]')) return;

    const max = note.maxLength > 0 ? note.maxLength : 200;
    const length = note.value.length;
    const panel = note.closest('.hh-note__panel');
    const count = panel?.querySelector('[data-hh-note-count]');
    if (count) count.textContent = `${length}/${max}`;

    // Screen readers hear the count only near the limit, not on every key.
    const live = panel?.querySelector('[data-hh-note-live]');
    if (live instanceof HTMLElement) {
      const left = max - length;
      const text = left <= 20 ? (live.dataset.template ?? '[count]').replace('[count]', String(left)) : '';
      if (live.textContent !== text) live.textContent = text;
    }
  };

  #handleClick = (/** @type {MouseEvent} */ event) => {
    if (!(event.target instanceof Element)) return;

    const swatch = event.target.closest('.hh-swatch');
    if (swatch instanceof HTMLElement) {
      event.preventDefault();
      this.#pickSwatch(swatch);
      return;
    }

    // A chip tapped again is unselected ("Who is it for?" is optional).
    // Click runs before change, so #recipient still holds the earlier choice here.
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-hh-recipient]')) {
      if (event.target.value === this.#recipient) {
        event.target.checked = false;
        this.#recipient = '';
      }
      return;
    }

    const noteToggle = event.target.closest('[data-hh-note-toggle]');
    if (noteToggle instanceof HTMLButtonElement) {
      const panel = document.getElementById(noteToggle.getAttribute('aria-controls') ?? '');
      if (!panel) return;
      const open = noteToggle.getAttribute('aria-expanded') !== 'true';
      noteToggle.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      if (open) panel.querySelector('textarea')?.focus();
      return;
    }

    const button = event.target.closest('[data-hh-set-add]');
    if (button instanceof HTMLButtonElement && !button.disabled) {
      event.preventDefault();
      this.addToCart(Number(button.dataset.hhSetAdd) || undefined);
      return;
    }

    // A tap anywhere else on a card (its watch photos sit outside the label) selects it, as its radio does.
    // Controls keep their own job; the label already selects the radio by itself.
    const card = event.target.closest('.hh-set-card');
    if (!card || event.target.closest('input, button, a, select, textarea, label, .hh-swatch, [role="radio"]')) return;
    const input = card.querySelector('.hh-set-card__input');
    if (input instanceof HTMLInputElement && !input.checked && !input.disabled) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /** Arrow keys move between swatches, like a radio group. */
  #handleKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (!(event.target instanceof HTMLElement) || !event.target.classList.contains('hh-swatch')) return;
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const step = keys[/** @type {keyof typeof keys} */ (event.key)];
    if (!step) return;

    const group = event.target.closest('.hh-swatches');
    const swatches = /** @type {HTMLElement[]} */ ([...(group?.querySelectorAll('.hh-swatch') ?? [])]);
    const index = swatches.indexOf(event.target);
    const next = swatches[(index + step + swatches.length) % swatches.length];
    if (!next) return;

    event.preventDefault();
    next.focus();
    this.#pickSwatch(next);
  };

  #syncQuantity = () => {
    this.#writeQuantity();
  };

  // ---------- colour picking ----------

  /** @param {HTMLElement} swatch */
  #pickSwatch(swatch) {
    if (swatch.dataset.available === 'false') return;

    const slot = swatch.closest('[data-hh-slot]');
    if (!(slot instanceof HTMLElement)) return;

    const variantId = swatch.dataset.variantId ?? '';
    const isFirst = slot.dataset.hhSlot === '1';

    if (isFirst) {
      // The first watch is shared by both cards; untouched second watches follow it.
      for (const first of this.querySelectorAll('[data-hh-slot="1"]')) {
        if (first instanceof HTMLElement) this.#setSlot(first, variantId);
      }
      for (const other of this.querySelectorAll('[data-hh-slot]:not([data-hh-slot="1"]):not([data-touched])')) {
        if (other instanceof HTMLElement) this.#setSlot(other, variantId);
      }
      this.dataset.variantId = variantId;
    } else {
      slot.dataset.touched = 'true';
      this.#setSlot(slot, variantId);
    }

    // Make sure the card the swatch belongs to is the selected one.
    const input = this.#optionOf(slot)?.querySelector('.hh-set-card__input');
    if (input instanceof HTMLInputElement && !input.checked) input.checked = true;

    this.#apply();
    if (isFirst) this.#syncGallery();
  }

  /**
   * Shows the given variant in a watch row: checked swatch, photo and colour name.
   * @param {HTMLElement} slot
   * @param {string} variantId
   */
  #setSlot(slot, variantId) {
    const swatch = this.#swatchFor(slot, variantId);
    if (!swatch) return;

    slot.dataset.variantId = variantId;
    for (const other of slot.querySelectorAll('.hh-swatch')) {
      other.setAttribute('aria-checked', String(other === swatch));
      other.setAttribute('tabindex', other === swatch ? '0' : '-1');
    }

    const image = slot.querySelector('.hh-watch__image');
    if (image instanceof HTMLImageElement && swatch.dataset.image) {
      image.removeAttribute('srcset');
      image.src = swatch.dataset.image;
    }

    const color = slot.querySelector('[data-hh-slot-color]');
    if (color) color.textContent = swatch.dataset.color ?? '';
  }

  /** Selects the first watch's colour in Horizon's variant picker, so the gallery shows it. */
  #syncGallery() {
    const option = this.#selectedOption;
    const slot = option ? this.#slots(option).find((element) => element.dataset.hhSlot === '1') : null;
    const variantId = slot instanceof HTMLElement ? slot.dataset.variantId : null;
    if (!slot || !variantId) return;

    const valueId = this.#swatchFor(slot, variantId)?.dataset.valueId;
    if (!valueId) return;

    const input = this.#section?.querySelector(`variant-picker input[data-option-value-id="${CSS.escape(valueId)}"]`);
    if (!(input instanceof HTMLInputElement) || input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @param {number} quantity */
  #select(quantity) {
    const input = this.querySelector(`.hh-set-card__input[value="${quantity}"]`);
    if (input instanceof HTMLInputElement && !input.checked) {
      input.checked = true;
      this.#apply();
      this.#syncGallery();
    }
  }

  // ---------- state ----------

  /** Updates cards, buttons and Horizon's form for the current choice, and tells the page. */
  #apply() {
    for (const slot of this.querySelectorAll('[data-hh-slot]')) {
      if (!(slot instanceof HTMLElement)) continue;
      // Keyboard focus lands on the checked swatch of each group.
      for (const swatch of slot.querySelectorAll('.hh-swatch')) {
        swatch.setAttribute('tabindex', swatch.getAttribute('aria-checked') === 'true' ? '0' : '-1');
      }
    }

    const selection = this.selection;
    if (!selection) return;

    for (const option of this.querySelectorAll('.hh-set-option')) {
      option.classList.toggle('is-selected', option.querySelector('.hh-set-card__input:checked') !== null);
    }
    this.#placeDetails();
    this.#moveExtras();

    this.#updateButtons(selection);
    this.#section?.classList.toggle('hh-mixed-set', selection.mixed);

    const quantitySelector = this.#writeQuantity();
    // Keeps Horizon's own listeners (price per item, Horizon sticky bar) in sync.
    (quantitySelector ?? this).dispatchEvent(new QuantitySelectorUpdateEvent(selection.quantity));

    this.dispatchEvent(new CustomEvent(HHBundlePicker.changeEvent, { bubbles: true, detail: selection }));
  }

  /**
   * Keeps "Who is it for?" and the gift note right above the selected card's button,
   * or in the "Personalise" fold on phones.
   */
  #moveExtras() {
    const extras = this.querySelector('[data-hh-extras-content]');
    const fold = this.#phone.matches ? this.querySelector('[data-hh-personalise-extras]') : null;
    const slot = fold ?? this.#selectedOption?.querySelector('[data-hh-extras]');
    if (!extras || !slot || extras.parentElement === slot) return;
    HHBundlePicker.#move(extras, () => slot.append(extras));
  }

  /** Phones: the selected card's watch rows sit in the "Personalise" fold; every other card keeps its own. */
  #placeDetails() {
    const fold = this.#phone.matches ? this.querySelector('[data-hh-personalise-details]') : null;
    const selected = this.#selectedOption;
    for (const option of this.querySelectorAll('.hh-set-option')) {
      const details = this.#detailsOf(option);
      const card = option.querySelector('.hh-set-card');
      if (!details || !card) continue;
      if (fold && option === selected) {
        if (details.parentElement !== fold) HHBundlePicker.#move(details, () => fold.append(details));
      } else if (details.parentElement !== card) {
        HHBundlePicker.#move(details, () => card.insertBefore(details, card.querySelector('.hh-set-option__cta')));
      }
    }
  }

  /**
   * Moves an element without losing keyboard focus inside it.
   * @param {Element} element
   * @param {() => void} move
   */
  static #move(element, move) {
    const focused = element.contains(document.activeElement) ? document.activeElement : null;
    move();
    if (focused instanceof HTMLElement) focused.focus({ preventScroll: true });
  }

  /** @param {BundleSelection} selection */
  #updateButtons(selection) {
    const button = this.#selectedOption?.querySelector('[data-hh-set-add]');
    if (!(button instanceof HTMLButtonElement)) return;

    const text = button.querySelector('.hh-set-option__button-text');
    if (text && button.dataset.labelAvailable === undefined) button.dataset.labelAvailable = text.innerHTML;

    button.disabled = !selection.available;
    if (text) {
      if (selection.available) text.innerHTML = button.dataset.labelAvailable ?? text.innerHTML;
      else text.textContent = button.dataset.labelSoldOut ?? '';
    }

    // The phone add button reads like the selected card's own button.
    const mobileButton = this.querySelector('[data-hh-mobile-button]');
    const mobileText = mobileButton?.querySelector('.hh-set-option__button-text');
    if (!(mobileButton instanceof HTMLButtonElement) || !mobileText) return;
    mobileButton.disabled = !selection.available;
    const label = selection.available ? button.dataset.labelAvailable ?? '' : '';
    if (selection.available) {
      if (mobileText.innerHTML !== label) mobileText.innerHTML = label;
    } else {
      mobileText.textContent = mobileButton.dataset.labelSoldOut ?? '';
    }
  }

  /** @returns {HTMLElement | null} the quantity selector component, if any */
  #writeQuantity() {
    const selection = this.selection;
    const form = this.#productForm;
    if (!selection || !form) return null;

    const value = String(selection.quantity);
    const quantitySelector = /** @type {HTMLElement & {setValue?: Function, updateButtonStates?: Function} | null} */ (
      form.querySelector('quantity-selector-component')
    );
    const quantityInput = form.querySelector('input[name="quantity"]');

    if (quantitySelector && typeof quantitySelector.setValue === 'function') {
      quantitySelector.setValue(value);
      quantitySelector.updateButtonStates?.();
    } else if (quantityInput instanceof HTMLInputElement) {
      // Horizon's component isn't upgraded yet; the plain input is what gets submitted.
      quantityInput.value = value;
    }

    return quantitySelector;
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    for (const button of this.querySelectorAll('[data-hh-set-add]')) {
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }
  }

  /** @param {string} message */
  #showError(message) {
    for (const element of this.querySelectorAll('.hh-set-option__error')) {
      if (!(element instanceof HTMLElement)) continue;
      element.textContent = message;
      element.hidden = !message;
    }
  }

  // ---------- cart ----------

  /**
   * Posts the set to the cart and fires Horizon's cart event, so the cart drawer,
   * cart count and other listeners update exactly as after Horizon's own add.
   * @param {BundleItem[]} items
   * @param {number} itemCount
   * @returns {Promise<boolean>} true when the watches were added
   */
  async #addItems(items, itemCount) {
    const routes = HHBundlePicker.#routes();
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    const detail = {
      source: 'hh-bundle-picker',
      sourceId: this.dataset.blockId ?? '',
      itemCount,
      productId: this.dataset.productId,
    };

    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items.map((item) => ({
            id: Number(item.id),
            quantity: item.quantity,
            ...(item.properties ? { properties: item.properties } : {}),
          })),
          sections: sectionIds.join(','),
        }),
      });
      const data = await response.json();
      const cart = await HHBundlePicker.#fetchCart(routes.cart_url);

      if (data?.status) {
        const message = data.description || data.message || '';
        this.#showError(message);
        this.dispatchEvent(new CartErrorEvent({ error: message || 'Add to cart failed', code: 'INVALID' }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, items: cart.items, didError: true },
        });
        return false;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detail, items: cart.items, sections: data?.sections, didError: false },
      });
      return true;
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
      return false;
    }
  }

  /** Opens the cart drawer (or the cart page when the theme uses one). */
  #afterAdd() {
    if (this.dataset.openDrawer !== 'true') return;

    const drawer = /** @type {HTMLElement & {open?: Function, isOpen?: boolean} | null} */ (
      document.querySelector('theme-drawer#cart-drawer')
    );

    if (drawer) {
      if (!drawer.isOpen && typeof drawer.open === 'function') drawer.open();
      return;
    }

    window.location.href = HHBundlePicker.#routes().cart_url;
  }

  /** @param {string} cartUrl */
  static async #fetchCart(cartUrl) {
    const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  }

  /** @returns {{cart_add_url: string, cart_url: string}} */
  static #routes() {
    // `Theme` is Horizon's global settings object (snippets/scripts.liquid).
    const routes = typeof Theme !== 'undefined' ? Theme.routes : null;
    return {
      cart_add_url: routes?.cart_add_url ?? '/cart/add.js',
      cart_url: routes?.cart_url ?? '/cart',
    };
  }

  #handleVariantRefresh = (/** @type {Event} */ event) => {
    if (!(event instanceof CustomEvent)) return;
    const html = event.detail?.html;
    if (!(html instanceof Document)) return;

    const fresh = html.querySelector(`hh-bundle-picker[data-block-id="${this.dataset.blockId}"]`);
    if (!(fresh instanceof HTMLElement)) return;

    for (const input of this.querySelectorAll('.hh-set-card__input')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const freshInput = fresh.querySelector(`.hh-set-card__input[value="${input.value}"]`);
      if (!(freshInput instanceof HTMLInputElement)) continue;
      input.dataset.total = freshInput.dataset.total ?? '';
      input.dataset.compare = freshInput.dataset.compare ?? '';
      input.dataset.percent = freshInput.dataset.percent ?? '0';
    }

    this.#apply();
  };
}

if (!customElements.get('hh-bundle-picker')) {
  customElements.define('hh-bundle-picker', HHBundlePicker);
}
